import React, { useState, useEffect, useRef } from "react";
import { apiFetch } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function LoginModal({ onClose }) {
  const { login } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    if (step === "code" && codeRef.current) codeRef.current.focus();
  }, [step]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    if (!phone.trim()) return;

    setLoading(true);
    setStatus(null);
    try {
      const data = await apiFetch("/api/auth/request-code", {
        method: "POST",
        body: { phone: phone.trim() },
      });
      setStep("code");
      setResendIn(data.resendInSec || 60);
      setStatus({ type: "success", message: data.message });
    } catch (err) {
      if (err.status === 429 && err.data?.retryAfterSec) setResendIn(err.data.retryAfterSec);
      setStatus({ type: "error", message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;

    setLoading(true);
    setStatus(null);
    try {
      const data = await apiFetch("/api/auth/verify-code", {
        method: "POST",
        body: { phone: phone.trim(), code },
      });
      login(data.token, data.user);
      onClose();
    } catch (err) {
      const left = err.data?.attemptsLeft;
      setStatus({
        type: "error",
        message: left !== undefined ? `${err.message} (נותרו ${left} ניסיונות)` : err.message,
      });
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          &times;
        </button>
        <div className="modal-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        {step === "phone" ? (
          <>
            <h2>התחברות</h2>
            <p className="modal-desc">
              הכנס את מספר הטלפון שלך ונשלח לך קוד אימות ב-SMS
            </p>
            <form onSubmit={requestCode}>
              <input
                type="tel"
                placeholder="050-1234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="modal-input"
                dir="ltr"
                autoFocus
              />
              <button
                type="submit"
                className="modal-submit"
                disabled={loading || !phone.trim()}
              >
                {loading ? "שולח..." : "שלח קוד אימות"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2>הזן את הקוד</h2>
            <p className="modal-desc">
              שלחנו קוד בן 6 ספרות אל <span dir="ltr">{phone}</span>
            </p>
            <form onSubmit={verifyCode}>
              <input
                ref={codeRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="------"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="modal-input otp-input"
                dir="ltr"
              />
              <button
                type="submit"
                className="modal-submit"
                disabled={loading || code.length !== 6}
              >
                {loading ? "מאמת..." : "התחבר"}
              </button>
            </form>
            <button
              type="button"
              className="otp-resend"
              onClick={requestCode}
              disabled={loading || resendIn > 0}
            >
              {resendIn > 0 ? `שליחה חוזרת בעוד ${resendIn} שניות` : "שלח קוד מחדש"}
            </button>
          </>
        )}

        {status && <div className={`modal-status ${status.type}`}>{status.message}</div>}
      </div>
    </div>
  );
}

export default LoginModal;
